import React, { useState } from 'react';
import {
  Button,
  Container,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Grid,
  useTheme,
  Divider,
} from '@mui/material';
import { CheckCircle } from 'lucide-react';
import { useAuth } from "../context/AuthContext";
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { logIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await logIn(email, password);
      navigate('/dashboard');
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={{ background: '#FDF8F3', minHeight: '100vh' }}>
      {/* Modern Navbar */}
      <AppBar position="fixed" elevation={0} sx={{ background: '#0D47A1', py: 1 }}>
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/icon.png" alt="Apply-Bridge" height="32" />
              <Typography variant="h6" bgcolor="black" sx={{ fontWeight: 600 }}>
                apply-bridge
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <a
                href="https://www.producthunt.com/posts/apply-bridge?embed=true&utm_source=badge-featured&utm_medium=badge&utm_souce=badge-apply&#0045;bridge"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=944041&theme=light&t=1742434485235"
                  alt="apply&#0045;bridge - Automate&#0032;your&#0032;job&#0032;search&#0032;with&#0032;AI&#0045;powered&#0032;smart&#0032;follow&#0045;ups&#0046; | Product Hunt"
                  width="150"
                  height="35"
                  style={{ display: 'block' }}
                />
              </a>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" onClick={() => navigate('/login')}
                sx={{
                  color: 'black',
                  background: '#42A5F5',
                  borderColor: 'black',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                }}>
                Login
              </Button>
              <Button variant="contained" onClick={() => navigate('/register')}
                sx={{
                  background: '#FF9138',
                  bgcolor: 'black',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                }}>
                Sign up for free
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="xl" sx={{ pt: 12, pb: 8 }}>
        <Grid container spacing={8} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h1"
              sx={{
                fontSize: { xs: '3rem', md: '4.5rem' },
                fontWeight: 700,
                lineHeight: 1.1,
                mb: 4
              }}>
              Find your next
              <br />
              job opportunity
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary', fontWeight: 400 }}>
              Automate your job search with AI-powered emails and smart follow-ups. Used by students and early career professionals.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" size="large" onClick={() => navigate('/register')}
                sx={{
                  bgcolor: 'black',
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                }}>
                Sign up for free
              </Button>
              <Button variant="outlined" size="large" onClick={() => navigate('/interactive-demo')}
                sx={{
                  color: 'black',
                  borderColor: 'black',
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  '&:hover': { borderColor: 'black', background: 'rgba(0,0,0,0.05)' }
                }}>
                See how it works
              </Button>
            </Box>

            {/* Social Proof */}
            <Box sx={{ mt: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span key={star} style={{ color: '#FFB400', fontSize: '20px' }}>★</span>
                ))}
                <Typography sx={{ ml: 1, color: 'text.secondary' }}>
                  4.8/5
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 3 }}>
                {['Google', 'Amazon', 'Apple', 'Meta'].map((company) => (
                  <img
                    key={company}
                    src={`/company-logos/${company.toLowerCase()}.png`}
                    alt={company}
                    style={{ height: 20, opacity: 0.6 }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Product Screenshot */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                background: 'radial-gradient(circle at center, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0) 70%)',
                borderRadius: '20px',
                transform: 'translateY(20px)',
                filter: 'blur(40px)',
                zIndex: -1
              }
            }}>
              <img
                src="/dashboard-preview.png"
                alt="Apply-Bridge Dashboard"
                style={{
                  width: '100%',
                  borderRadius: '20px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* REVIEWS SECTION */}


      {/* Tracking Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 12 }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            {/* Table Image */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                bgcolor: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              }}>
                <img
                  src="/tracking-table.png"
                  alt="Email tracking interface"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
            </Grid>

            {/* Feature Description */}
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3
              }}>
                Track Your Applications
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
                Stay organized with our intuitive tracking system. Monitor all your job application outreach, wait periods, and response statuses in one place.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  {
                    title: 'Smart Wait Period Tracking',
                    desc: 'Automatically tracks how long since your last interaction'
                  },
                  {
                    title: 'Status Monitoring',
                    desc: 'See which applications are active or need follow-up'
                  },
                  {
                    title: 'Reply Tracking',
                    desc: 'Instantly know when you receive responses from recruiters'
                  }
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                    <CheckCircle
                      style={{
                        width: 24,
                        height: 24,
                        color: theme.palette.primary.main,
                        marginTop: 4
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ bgcolor: 'white', py: 12 }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            {/* Feature Description */}
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3
              }}>
                Automated Follow-ups
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
                Never miss following up on an application. Our intelligent system automates the entire process based on recruiter responses and customizable wait periods.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  {
                    title: 'Smart Wait Timer',
                    desc: 'Set custom wait periods for each application and let our system track the timing'
                  },
                  {
                    title: 'Response Detection',
                    desc: 'Automatically detects if youve received a response from the recruiter'
                  },
                  {
                    title: 'Automated Follow-ups',
                    desc: 'Sends perfectly timed follow-up emails when no response is received'
                  },
                  {
                    title: 'Customizable Workflows',
                    desc: 'Tailor the automation process to match your job search strategy'
                  }
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                    <CheckCircle
                      style={{
                        width: 24,
                        height: 24,
                        color: theme.palette.primary.main,
                        marginTop: 4
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Workflow Image */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}>
                <img
                  src="/workflow-automation.png"
                  alt="Email automation workflow"
                  style={{
                    width: '80%',
                    height: '80%',
                    display: 'block'
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Chrome Extension Section */}
      <Box sx={{ background: '#FDF8F3', py: 12 }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            {/* Extension Screenshot - Left side */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '10%',
                  left: '10%',
                  right: '10%',
                  bottom: '-10%',
                  background: 'linear-gradient(180deg, rgba(66, 133, 244, 0.2) 0%, rgba(66, 133, 244, 0.1) 100%)',
                  borderRadius: '24px',
                  transform: 'rotate(-3deg)',
                  zIndex: 0
                }
              }}>
                <img
                  src="/chrome-extension.png"
                  alt="ApplyBridge Chrome Extension"
                  style={{
                    width: '80%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    position: 'relative',
                    zIndex: 1
                  }}
                />
              </Box>
            </Grid>

            {/* Description - Right side */}
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3
              }}>
                Chrome Extension
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
                Supercharge your job search with our intelligent Chrome extension. Analyze job postings and find recruiter contact information in seconds.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  {
                    title: 'One-Click Analysis',
                    desc: 'Analyze any job posting with a single click directly from the job board'
                  },
                  {
                    title: 'Find LinkedIn Recruiters',
                    desc: 'Automatically identify recruiters associated with the job posting'
                  },
                  {
                    title: 'Email Discovery',
                    desc: 'Find recruiter email addresses instantly with our email finder'
                  },
                  {
                    title: 'Works Everywhere',
                    desc: 'Compatible with LinkedIn, Indeed, and other major job boards'
                  }
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                    <CheckCircle
                      style={{
                        width: 24,
                        height: 24,
                        color: theme.palette.primary.main,
                        marginTop: 4
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  href="https://chromewebstore.google.com/detail/applybridge/pehhplblehhcbehajdocbfcbgkemdknd"
                  target="_blank"
                  sx={{
                    bgcolor: 'black',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                  }}
                >
                  Add to Chrome
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Email Integration Section */}
      <Box sx={{ bgcolor: '#f8f9fa', py: 12 }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            {/* Description - Left side */}
            <Grid item xs={12} md={6}>
              <Typography variant="h2" sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 3
              }}>
                Connect Your Email
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
                Seamlessly integrate with your preferred email provider. Send and track job applications directly from your Gmail or Outlook account.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  {
                    title: 'Easy Integration',
                    desc: 'Connect your email account with just a few clicks'
                  },
                  {
                    title: 'Secure Authentication',
                    desc: 'Industry-standard security protocols to protect your data'
                  },
                  {
                    title: 'Send from Your Email',
                    desc: 'All emails are sent directly from your personal account'
                  },
                  {
                    title: 'Multiple Account Support',
                    desc: 'Connect and manage multiple email accounts in one place'
                  }
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                    <CheckCircle
                      style={{
                        width: 24,
                        height: 24,
                        color: theme.palette.primary.main,
                        marginTop: 4
                      }}
                    />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {feature.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Email Integration Image - Right side */}
            <Grid item xs={12} md={6}>
              <Box sx={{
                display: 'flex',
                gap: 4,
                justifyContent: 'center'
              }}>
                {/* Gmail */}
                <Box sx={{
                  p: 4,
                  bgcolor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  width: '200px'
                }}>
                  <img
                    src="/gmail-icon.png"
                    alt="Gmail"
                    style={{
                      width: '96px',
                      height: '64px'
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Gmail
                  </Typography>
                </Box>

                {/* Outlook */}
                <Box sx={{
                  p: 4,
                  bgcolor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  width: '200px'
                }}>
                  <img
                    src="/outlook-icon.png"
                    alt="Outlook"
                    style={{
                      width: '64px',
                      height: '64px'
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Outlook
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box sx={{ bgcolor: '#ffffff', py: 12 }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 3
            }}>
              What Our Users Say
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}>
              Join hundreds of job seekers who have streamlined their application process with apply-bridge
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 6
          }}>
            <iframe
              title="review_1"
              src="https://cards.producthunt.com/cards/comments/4484983?v=1"
              width="500"
              height="405"
              frameborder="0"
              scrolling="no"
              allowfullscreen></iframe>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              href="https://www.producthunt.com/posts/apply-bridge"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'black',
                borderColor: 'black',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                '&:hover': { borderColor: 'black', background: 'rgba(0,0,0,0.05)' }
              }}
            >
              See more reviews on Product Hunt
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;